import { useCallback, useRef } from 'react';

import { AlgunaCheckoutEmbed } from '@components/features/AlgunaCheckout/AlgunaCheckoutEmbed';
import {
  AlgunaCheckoutController,
  ReadyCheckout,
  useAlgunaCheckout,
} from '@components/features/AlgunaCheckout/useAlgunaCheckout';

import { CheckoutStatus } from './CheckoutStatus';
import { UpgradeDrawerBody } from './UpgradeDrawer';

import css from './styles.module.css';

type UpgradeCheckoutProps = {
  teamUuid: string;
  paid: boolean;
  completed: boolean;
  title?: string;
  subText?: string;
  onPaid: () => void;
  onComplete: () => void;
  onDismiss: () => void;
};

type AlgunaCheckoutProps = {
  checkout: AlgunaCheckoutController;
  onScroll: (deltaY: number) => void;
};

type CheckoutFormProps = {
  checkout: ReadyCheckout;
  onScroll: (deltaY: number) => void;
};

function AlgunaCheckout({ checkout, onScroll }: AlgunaCheckoutProps) {
  const { state } = checkout;

  if (state.status === 'paid' && state.confirmationExhausted) {
    return (
      <div className={css.fill}>
        <CheckoutStatus variant="processing" />
      </div>
    );
  }

  // Pro checkout is never unavailable; that's for plans like ASV Scans
  if (
    state.status === 'paid' ||
    state.status === 'loading' ||
    state.status === 'active' ||
    state.status === 'unavailable'
  ) {
    return null;
  }

  if (state.status === 'error') {
    return (
      <div className={css.fill}>
        <CheckoutStatus
          variant={state.incomplete ? 'incomplete' : 'error'}
          onRetry={state.retry}
        />
      </div>
    );
  }

  return <CheckoutForm checkout={state} onScroll={onScroll} />;
}

function CheckoutForm({ checkout, onScroll }: CheckoutFormProps) {
  return (
    <div className={css.checkout}>
      <div className={css.stage}>
        <AlgunaCheckoutEmbed
          embed={checkout.embed}
          className={css.embed}
          onScroll={onScroll}
        />
      </div>
    </div>
  );
}

export function UpgradeCheckout({
  teamUuid,
  paid,
  completed,
  title,
  subText,
  onPaid,
  onComplete,
  onDismiss,
}: UpgradeCheckoutProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const checkout = useAlgunaCheckout(teamUuid, {
    plan: 'pro',
    paid,
    onPaid,
    onComplete,
  });

  const onScroll = useCallback((deltaY: number) => {
    scrollRef.current?.scrollBy(0, deltaY);
  }, []);

  if (completed || checkout.state.status === 'active') {
    return (
      <UpgradeDrawerBody
        completed
        scrollRef={scrollRef}
        title={title}
        dismissible={!paid}
        onDismiss={onDismiss}
      />
    );
  }

  return (
    <UpgradeDrawerBody
      scrollRef={scrollRef}
      title={title}
      subText={subText}
      activity={checkout.activity}
      dismissible={!paid}
      canSubmit={checkout.canSubmit}
      isProcessing={checkout.isProcessing}
      onSubmit={checkout.submit}
      onDismiss={onDismiss}
    >
      <AlgunaCheckout checkout={checkout} onScroll={onScroll} />
    </UpgradeDrawerBody>
  );
}
